import { useState, useEffect, useCallback } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useAuth } from '@/contexts/AuthContext';
import { useLanguage } from '@/i18n/LanguageContext';
import { createCreditCardPayment } from '@/services/api';
import { Button } from '@/components/ui/button';
import { Card, CardContent } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import {
  Dialog, DialogContent, DialogDescription, DialogFooter,
  DialogHeader, DialogTitle
} from '@/components/ui/dialog';
import { formatCurrency } from '@/lib/currency';
import {
  Plus, LogOut, LayoutDashboard, Receipt, Settings, ShieldCheck, Menu, X,
  Wallet, CreditCard, Banknote, Smartphone, Trash2, Pencil, Target, Coins, PiggyBank, Tag, Repeat,
  BarChart3, Upload, Image as ImageIcon
} from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const TYPE_META = {
  cash:           { label: 'Efectivo',         icon: Banknote,   color: '#10B981' },
  bank:           { label: 'Cuenta Bancaria',  icon: Wallet,     color: '#3B82F6' },
  credit_card:    { label: 'Tarjeta de Crédito', icon: CreditCard, color: '#EC4899' },
  digital_wallet: { label: 'Billetera Digital', icon: Smartphone, color: '#F59E0B' },
  investment:     { label: 'Inversiones',      icon: Coins,      color: '#14B8A6' },
};

const CURRENCY_OPTIONS = [
  { value: 'BRL', label: 'Real brasileño (BRL)' },
  { value: 'USD', label: 'Dólar estadounidense (USD)' },
  { value: 'COP', label: 'Peso colombiano (COP)' },
  { value: 'EUR', label: 'Euro (EUR)' },
  { value: 'GBP', label: 'Libra esterlina (GBP)' },
  { value: 'MXN', label: 'Peso mexicano (MXN)' },
  { value: 'PEN', label: 'Sol peruano (PEN)' },
  { value: 'CLP', label: 'Peso chileno (CLP)' },
  { value: 'ARS', label: 'Peso argentino (ARS)' },
];

const AccountsPage = () => {
  const { user, logout, isAdmin, currency, locale } = useAuth();
  const navigate = useNavigate();
  const fmt = (v) => formatCurrency(v, currency, locale);
  const fmtMoney = (value, moneyCurrency = currency) => formatCurrency(value || 0, moneyCurrency || currency, locale);

  const [accounts, setAccounts]   = useState([]);
  const [loading, setLoading]     = useState(true);
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [statementAccount, setStatementAccount] = useState(null);
  const [statement, setStatement] = useState(null);
  const [paymentDialogOpen, setPaymentDialogOpen] = useState(false);
  const [paymentCard, setPaymentCard] = useState(null);
  const [paymentSaving, setPaymentSaving] = useState(false);
  const [paymentError, setPaymentError] = useState('');
  const [paymentForm, setPaymentForm] = useState({
    from_account_id: '',
    amount: '',
    date: new Date().toISOString().slice(0, 10),
    description: ''
  });
  const [form, setForm] = useState({
    name: '', type: 'cash', initial_balance: '', control_start_date: '', currency,
    credit_limit: '', statement_closing_day: '', payment_due_day: '',
    color: '#7C3AED'
  });

  const fetchAccounts = useCallback(async () => {
    setLoading(true);
    try {
      const token = localStorage.getItem('token');
      const res = await fetch(`${API_URL}/api/accounts`,
        { headers: { Authorization: `Bearer ${token}` } });
      if (res.ok) setAccounts(await res.json());
    } finally { setLoading(false); }
  }, []);

  useEffect(() => { fetchAccounts(); }, [fetchAccounts]);

  const openCreate = () => {
    setEditing(null);
    setForm({
      name: '', type: 'cash', initial_balance: '', control_start_date: '',
      currency,
      credit_limit: '', statement_closing_day: '', payment_due_day: '',
      color: '#7C3AED'
    });
    setDialogOpen(true);
  };

  const openEdit = (acc) => {
    setEditing(acc);
    setForm({
      name: acc.name,
      type: acc.type,
      initial_balance: String(acc.initial_balance),
      control_start_date: acc.control_start_date || '',
      currency: acc.currency || currency,
      credit_limit: acc.credit_limit != null ? String(acc.credit_limit) : '',
      statement_closing_day: acc.statement_closing_day != null ? String(acc.statement_closing_day) : '',
      payment_due_day: acc.payment_due_day != null ? String(acc.payment_due_day) : '',
      color: acc.color || '#7C3AED'
    });
    setDialogOpen(true);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    const token = localStorage.getItem('token');
    const body = {
      name: form.name,
      initial_balance: form.initial_balance ? parseFloat(form.initial_balance) : 0,
      control_start_date: form.control_start_date || null,
      currency: form.currency || currency,
      color: form.color
    };

    if (form.type === 'credit_card') {
      body.credit_limit = form.credit_limit ? parseFloat(form.credit_limit) : null;
      body.statement_closing_day = form.statement_closing_day ? parseInt(form.statement_closing_day, 10) : null;
      body.payment_due_day = form.payment_due_day ? parseInt(form.payment_due_day, 10) : null;
    }

    try {
      if (editing) {
        await fetch(`${API_URL}/api/accounts/${editing.id}`, {
          method: 'PATCH',
          headers: { 'Authorization': `Bearer ${token}`, 'Content-Type': 'application/json' },
          body: JSON.stringify(body)
        });
      } else {
        body.type = form.type;
        await fetch(`${API_URL}/api/accounts`, {
          method: 'POST',
          headers: { 'Authorization': `Bearer ${token}`, 'Content-Type': 'application/json' },
          body: JSON.stringify(body)
        });
      }
      setDialogOpen(false);
      fetchAccounts();
    } catch (err) {
      console.error('Error saving account');
    }
  };

  const handleDelete = async (acc) => {
    if (!window.confirm(`¿Eliminar la cuenta "${acc.name}"? Las transacciones se conservarán.`)) return;
    const token = localStorage.getItem('token');
    await fetch(`${API_URL}/api/accounts/${acc.id}`, {
      method: 'DELETE',
      headers: { Authorization: `Bearer ${token}` }
    });
    fetchAccounts();
  };

  const handleViewStatement = async (acc) => {
    const token = localStorage.getItem('token');
    const res = await fetch(`${API_URL}/api/accounts/${acc.id}/statement`,
      { headers: { Authorization: `Bearer ${token}` } });
    if (res.ok) {
      setStatement(await res.json());
      setStatementAccount(acc);
    }
  };

  const paymentSourceAccounts = accounts.filter(acc => acc.type !== 'credit_card' && acc.is_active !== false);

  const openPaymentDialog = (acc) => {
    const defaultSource = paymentSourceAccounts.find(a => a.type === 'bank') || paymentSourceAccounts[0];
    setPaymentCard(acc);
    setPaymentError('');
    setPaymentForm({
      from_account_id: defaultSource?.id || '',
      amount: acc.balance < 0 ? Math.abs(acc.balance).toFixed(2) : '',
      date: new Date().toISOString().slice(0, 10),
      description: `Pago tarjeta ${acc.name}`
    });
    setPaymentDialogOpen(true);
  };

  const handlePaymentSubmit = async (e) => {
    e.preventDefault();
    setPaymentError('');
    if (!paymentCard) return;
    if (!paymentForm.from_account_id) {
      setPaymentError('Selecciona la cuenta desde donde saldrá el pago.');
      return;
    }
    const amount = parseFloat(paymentForm.amount);
    if (!amount || amount <= 0) {
      setPaymentError('Ingresa un valor mayor que cero.');
      return;
    }

    try {
      setPaymentSaving(true);
      await createCreditCardPayment({
        from_account_id: paymentForm.from_account_id,
        credit_card_account_id: paymentCard.id,
        amount,
        date: paymentForm.date,
        description: paymentForm.description || `Pago tarjeta ${paymentCard.name}`
      });
      setPaymentDialogOpen(false);
      setPaymentCard(null);
      await fetchAccounts();
    } catch (err) {
      setPaymentError(err.response?.data?.detail || 'No fue posible registrar el pago.');
    } finally {
      setPaymentSaving(false);
    }
  };

  const handleLogout = () => { logout(); navigate('/login'); };

  return (
    <div className="min-h-screen bg-zinc-950 flex">
      {/* Mobile menu overlay */}
      {mobileMenuOpen && (
        <div className="fixed inset-0 z-50 lg:hidden" role="dialog" aria-label="Menú">
          <div className="fixed inset-0 bg-black/60" onClick={() => setMobileMenuOpen(false)} />
          <aside className="fixed left-0 top-0 bottom-0 w-72 max-w-[85vw] bg-zinc-900 border-r border-zinc-800 p-6 z-50 overflow-y-auto">
            <div className="flex items-center justify-between mb-8">
              <div>
                <h1 className="text-2xl font-black gradient-text">WSFinances</h1>
                <p className="text-zinc-500 text-sm mt-1">Panel de Finanzas</p>
              </div>
              <button onClick={() => setMobileMenuOpen(false)} className="text-zinc-300 p-2 -mr-2" aria-label="Cerrar menú">
                <X className="h-6 w-6" />
              </button>
            </div>
            <SidebarNav active="accounts" isAdmin={isAdmin} onClick={() => setMobileMenuOpen(false)} />
            <div className="mt-8 pt-6 border-t border-zinc-800">
              <div className="p-4 rounded-lg bg-zinc-800/50 border border-zinc-700 mb-4">
                <p className="text-sm text-zinc-400">Conectado como</p>
                <p className="font-medium text-white truncate">{user?.name}</p>
                <p className="text-xs text-zinc-500 truncate">{user?.email}</p>
              </div>
              <Button variant="ghost" className="w-full justify-start text-zinc-300 hover:text-red-400" onClick={handleLogout}>
                <LogOut className="h-4 w-4 mr-2" /> Cerrar Sesión
              </Button>
            </div>
          </aside>
        </div>
      )}

      {/* Desktop sidebar */}
      <aside className="w-64 sidebar border-r border-zinc-800 p-6 hidden lg:block relative min-h-screen">
        <div className="mb-8">
          <h1 className="text-2xl font-black gradient-text">WSFinances</h1>
          <p className="text-zinc-500 text-sm mt-1">Panel de Finanzas</p>
        </div>
        <SidebarNav active="accounts" isAdmin={isAdmin} />
        <div className="absolute bottom-6 left-6 right-6">
          <div className="p-4 rounded-lg bg-zinc-800/50 border border-zinc-700 mb-4">
            <p className="text-sm text-zinc-400">Conectado como</p>
            <p className="font-medium text-white truncate">{user?.name}</p>
            <p className="text-xs text-zinc-500 truncate">{user?.email}</p>
          </div>
          <Button variant="ghost" className="w-full justify-start text-zinc-300 hover:text-red-400" onClick={handleLogout}>
            <LogOut className="h-4 w-4 mr-2" /> Cerrar Sesión
          </Button>
        </div>
      </aside>

      <main className="flex-1 min-w-0 p-4 sm:p-6 lg:p-8 overflow-x-hidden">
        <div className="flex flex-col gap-4 mb-6 sm:flex-row sm:items-center sm:justify-between">
          <div className="flex items-center gap-3 min-w-0">
            <button className="lg:hidden text-zinc-300 p-2 -ml-2" onClick={() => setMobileMenuOpen(true)} aria-label="Abrir menú">
              <Menu className="h-6 w-6" />
            </button>
            <div className="min-w-0">
              <h2 className="text-2xl sm:text-3xl font-bold text-white">Cuentas</h2>
              <p className="text-zinc-400 text-sm">Efectivo, bancos, tarjetas, billeteras digitales e inversiones</p>
            </div>
          </div>
          <Button className="bg-violet-600 hover:bg-violet-700" onClick={openCreate} data-testid="new-account-btn">
            <Plus className="h-4 w-4 mr-2" /> Nueva cuenta
          </Button>
        </div>

        {loading ? (
          <p className="text-zinc-500">Cargando…</p>
        ) : accounts.length === 0 ? (
          <Card className="bg-gradient-to-br from-violet-500/10 to-pink-500/5 border-violet-500/30">
            <CardContent className="p-8 text-center">
              <div className="inline-flex h-14 w-14 rounded-full bg-violet-500/20 items-center justify-center mb-4">
                <Wallet className="h-7 w-7 text-violet-400" />
              </div>
              <h3 className="text-xl font-bold text-white mb-2">Aún no tienes cuentas</h3>
              <p className="text-zinc-400 mb-6">Crea tu primera cuenta para empezar a vincular transacciones.</p>
              <Button className="bg-violet-600 hover:bg-violet-700" onClick={openCreate}>
                <Plus className="h-4 w-4 mr-2" /> Crear primera cuenta
              </Button>
            </CardContent>
          </Card>
        ) : (
          <div className="grid grid-cols-1 sm:grid-cols-2 xl:grid-cols-3 gap-4">
            {accounts.map(acc => {
              const meta = TYPE_META[acc.type] || TYPE_META.cash;
              const Icon = meta.icon;
              const isCC = acc.type === 'credit_card';
              const accountCurrency = acc.currency || currency;
              const displayCurrency = acc.display_currency || currency;
              const hasConversion = accountCurrency !== displayCurrency && acc.balance_converted != null && !acc.conversion_missing;
              return (
                <Card key={acc.id} className="bg-zinc-900/60 border-zinc-800 hover:border-zinc-700 transition-colors">
                  <CardContent className="p-5">
                    <div className="flex items-start gap-3 mb-4">
                      <div className="h-11 w-11 rounded-full flex items-center justify-center shrink-0" style={{ backgroundColor: `${acc.color}33` }}>
                        <Icon className="h-5 w-5" style={{ color: acc.color }} />
                      </div>
                      <div className="min-w-0 flex-1">
                        <h3 className="font-semibold text-white truncate">{acc.name}</h3>
                        <p className="text-xs text-zinc-400">{meta.label}</p>
                      </div>
                      <div className="flex gap-1">
                        <Button variant="ghost" size="icon" className="h-8 w-8 text-zinc-400 hover:text-white" onClick={() => openEdit(acc)} aria-label="Editar cuenta">
                          <Pencil className="h-4 w-4" />
                        </Button>
                        <Button variant="ghost" size="icon" className="h-8 w-8 text-zinc-400 hover:text-red-400" onClick={() => handleDelete(acc)} aria-label="Eliminar cuenta">
                          <Trash2 className="h-4 w-4" />
                        </Button>
                      </div>
                    </div>

                    <div>
                      <p className="text-xs text-zinc-400 mb-1">
                        {isCC ? 'Saldo de la tarjeta' : 'Saldo actual'} · {accountCurrency}
                      </p>
                      <p className={`text-2xl font-bold truncate ${
                        isCC
                          ? (acc.balance < 0 ? 'text-red-400' : 'text-zinc-300')
                          : (acc.balance >= 0 ? 'text-white' : 'text-red-400')
                      }`}>
                        {fmtMoney(acc.balance, accountCurrency)}
                      </p>
                      {hasConversion && (
                        <p className="text-xs text-zinc-400 mt-1">
                          ≈ {fmtMoney(acc.balance_converted, displayCurrency)} en {displayCurrency}
                        </p>
                      )}
                    {accountCurrency !== displayCurrency && acc.conversion_missing && (
                        <p className="text-xs text-amber-300 mt-1">
                          Falta tasa para convertir a {displayCurrency}
                        </p>
                      )}
                      {acc.control_start_date && (
                        <p className="text-xs text-zinc-500 mt-1">
                          Desde {acc.control_start_date} · inicial {fmtMoney(acc.initial_balance, accountCurrency)}
                        </p>
                      )}
                    </div>

                    {isCC && (
                      <div className="mt-4 pt-4 border-t border-zinc-800 space-y-2 text-sm">
                        {acc.credit_limit != null && (
                          <div className="flex justify-between text-zinc-400">
                            <span>Límite</span>
                            <span className="text-zinc-200">{fmtMoney(acc.credit_limit, accountCurrency)}</span>
                          </div>
                        )}
                        {acc.statement_closing_day && (
                          <div className="flex justify-between text-zinc-400">
                            <span>Cierre de factura</span>
                            <span className="text-zinc-200">día {acc.statement_closing_day}</span>
                          </div>
                        )}
                        {acc.payment_due_day && (
                          <div className="flex justify-between text-zinc-400">
                            <span>Vencimiento</span>
                            <span className="text-zinc-200">día {acc.payment_due_day}</span>
                          </div>
                        )}
                        <Button
                          variant="outline"
                          className="border-zinc-700 hover:bg-zinc-800 text-zinc-200 w-full mt-2"
                          onClick={() => handleViewStatement(acc)}
                          data-testid={`statement-btn-${acc.id}`}
                        >
                          Ver factura actual
                        </Button>
                        <Button
                          className="bg-emerald-600 hover:bg-emerald-700 text-white w-full"
                          onClick={() => openPaymentDialog(acc)}
                          data-testid={`pay-card-btn-${acc.id}`}
                        >
                          Pagar tarjeta
                        </Button>
                      </div>
                    )}
                  </CardContent>
                </Card>
              );
            })}
          </div>
        )}

        {/* Create/Edit dialog */}
        <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
          <DialogContent className="bg-zinc-900 border-zinc-800 max-w-[95vw] sm:max-w-md">
            <DialogHeader>
              <DialogTitle>{editing ? 'Editar cuenta' : 'Nueva cuenta'}</DialogTitle>
              <DialogDescription>
                {editing ? 'Actualiza los datos de tu cuenta' : 'Crea una cuenta para clasificar tus transacciones'}
              </DialogDescription>
            </DialogHeader>
            <form onSubmit={handleSubmit} className="space-y-4">
              <div>
                <Label htmlFor="acc-name">Nombre</Label>
                <Input id="acc-name" required value={form.name}
                  onChange={(e) => setForm({ ...form, name: e.target.value })}
                  className="bg-zinc-800 border-zinc-700" placeholder="Ej: Itaú, Nubank, Efectivo, CDB / CDI" />
              </div>
              {!editing && (
                <div>
                  <Label htmlFor="acc-type">Tipo</Label>
                  <Select value={form.type} onValueChange={(v) => setForm({ ...form, type: v })}>
                    <SelectTrigger id="acc-type" className="bg-zinc-800 border-zinc-700">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="cash">Efectivo</SelectItem>
                      <SelectItem value="bank">Cuenta Bancaria</SelectItem>
                      <SelectItem value="credit_card">Tarjeta de Crédito</SelectItem>
                      <SelectItem value="digital_wallet">Billetera Digital</SelectItem>
                      <SelectItem value="investment">Inversiones</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
              )}
              <div>
                <Label htmlFor="acc-currency">Moneda de la cuenta</Label>
                <Select value={form.currency || currency} onValueChange={(v) => setForm({ ...form, currency: v })}>
                  <SelectTrigger id="acc-currency" className="bg-zinc-800 border-zinc-700">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {CURRENCY_OPTIONS.map(option => (
                      <SelectItem key={option.value} value={option.value}>{option.label}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
                <p className="text-xs text-zinc-500 mt-1">
                  Las transacciones de esta cuenta se interpretan en esta moneda.
                </p>
              </div>
              {form.type !== 'credit_card' && (
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                  <div>
                    <Label htmlFor="acc-balance">Saldo inicial</Label>
                    <Input id="acc-balance" inputMode="decimal" type="number" step="0.01"
                      value={form.initial_balance}
                      onChange={(e) => setForm({ ...form, initial_balance: e.target.value })}
                      className="bg-zinc-800 border-zinc-700" placeholder="0.00" />
                  </div>
                  <div>
                    <Label htmlFor="acc-control-date">Inicio de control</Label>
                    <Input id="acc-control-date" type="date"
                      value={form.control_start_date}
                      onChange={(e) => setForm({ ...form, control_start_date: e.target.value })}
                      className="bg-zinc-800 border-zinc-700" />
                  </div>
                  <p className="text-xs text-zinc-500 sm:col-span-2">
                    El saldo se calcula desde esta fecha. Los movimientos anteriores quedan como histórico y no afectan el saldo.
                  </p>
                </div>
              )}
              {form.type === 'credit_card' && (
                <>
                  <div>
                    <Label htmlFor="acc-limit">Límite de crédito</Label>
                    <Input id="acc-limit" inputMode="decimal" type="number" step="0.01"
                      value={form.credit_limit}
                      onChange={(e) => setForm({ ...form, credit_limit: e.target.value })}
                      className="bg-zinc-800 border-zinc-700" placeholder="5000.00" />
                  </div>
                  <div className="grid grid-cols-2 gap-3">
                    <div>
                      <Label htmlFor="acc-closing">Día de cierre</Label>
                      <Input id="acc-closing" inputMode="numeric" type="number" min="1" max="28"
                        value={form.statement_closing_day}
                        onChange={(e) => setForm({ ...form, statement_closing_day: e.target.value })}
                        className="bg-zinc-800 border-zinc-700" placeholder="15" />
                    </div>
                    <div>
                      <Label htmlFor="acc-due">Día de pago</Label>
                      <Input id="acc-due" inputMode="numeric" type="number" min="1" max="28"
                        value={form.payment_due_day}
                        onChange={(e) => setForm({ ...form, payment_due_day: e.target.value })}
                        className="bg-zinc-800 border-zinc-700" placeholder="22" />
                    </div>
                  </div>
                </>
              )}
              <div>
                <Label htmlFor="acc-color">Color</Label>
                <Input id="acc-color" type="color"
                  value={form.color}
                  onChange={(e) => setForm({ ...form, color: e.target.value })}
                  className="bg-zinc-800 border-zinc-700 h-10 w-full" />
              </div>
              <DialogFooter>
                <Button type="submit" className="bg-violet-600 hover:bg-violet-700" data-testid="save-account-btn">
                  {editing ? 'Guardar cambios' : 'Crear cuenta'}
                </Button>
              </DialogFooter>
            </form>
          </DialogContent>
        </Dialog>

        {/* Statement dialog */}
        <Dialog open={!!statement} onOpenChange={(o) => !o && setStatement(null)}>
          <DialogContent className="bg-zinc-900 border-zinc-800 max-w-[95vw] sm:max-w-lg max-h-[90vh] overflow-y-auto">
            <DialogHeader>
              <DialogTitle>Factura: {statementAccount?.name}</DialogTitle>
              <DialogDescription>
                Período {statement?.period_start} → {statement?.period_end}
              </DialogDescription>
            </DialogHeader>
            {statement && (
              <div className="space-y-4">
                <div className="grid grid-cols-2 gap-3">
                  <Card className="bg-zinc-800/50 border-zinc-700">
                    <CardContent className="p-4">
                      <p className="text-xs text-zinc-400">Factura actual</p>
                      <p className="text-2xl font-bold text-red-400">{fmt(statement.current_statement_total)}</p>
                      <p className="text-xs text-zinc-500 mt-1">{statement.current_statement_count} cargo(s)</p>
                    </CardContent>
                  </Card>
                  <Card className="bg-zinc-800/50 border-zinc-700">
                    <CardContent className="p-4">
                      <p className="text-xs text-zinc-400">Cuotas futuras</p>
                      <p className="text-2xl font-bold text-amber-400">{fmt(statement.future_installments_total)}</p>
                      <p className="text-xs text-zinc-500 mt-1">{statement.future_installments.length} cuota(s)</p>
                    </CardContent>
                  </Card>
                </div>
                {statement.payment_due_date && (
                  <p className="text-sm text-zinc-400">
                    Próximo vencimiento: <span className="text-white font-medium">{statement.payment_due_date}</span>
                  </p>
                )}
                {statement.credit_limit && (
                  <div className="bg-zinc-800/50 rounded-lg p-3 text-sm">
                    <div className="flex justify-between mb-1">
                      <span className="text-zinc-400">Límite disponible</span>
                      <span className="text-zinc-200">{fmt(statement.available_limit)} / {fmt(statement.credit_limit)}</span>
                    </div>
                    <div className="h-2 bg-zinc-700 rounded-full">
                      <div className="h-full bg-violet-500 rounded-full"
                        style={{ width: `${Math.min(100, ((statement.credit_limit - statement.available_limit) / statement.credit_limit) * 100)}%` }} />
                    </div>
                  </div>
                )}
                {statement.future_installments.length > 0 && (
                  <div>
                    <h4 className="text-sm font-semibold text-zinc-300 mb-2">Próximas cuotas</h4>
                    <ul className="space-y-1 text-sm">
                      {statement.future_installments.map(t => (
                        <li key={t.id} className="flex justify-between text-zinc-300 py-1 border-b border-zinc-800">
                          <span className="truncate">
                            {t.description}
                            <span className="text-zinc-500 ml-2">({t.installments_current}/{t.installments_total})</span>
                          </span>
                          <span className="text-zinc-400 shrink-0 ml-3">{t.date} · {fmt(t.amount)}</span>
                        </li>
                      ))}
                    </ul>
                  </div>
                )}
              </div>
            )}
          </DialogContent>
        </Dialog>

        {/* Credit card payment dialog */}
        <Dialog open={paymentDialogOpen} onOpenChange={setPaymentDialogOpen}>
          <DialogContent className="bg-zinc-900 border-zinc-800 max-w-[95vw] sm:max-w-md">
            <DialogHeader>
              <DialogTitle>Pagar tarjeta</DialogTitle>
              <DialogDescription>
                Registra el pago como transferencia hacia {paymentCard?.name}.
              </DialogDescription>
            </DialogHeader>
            <form onSubmit={handlePaymentSubmit} className="space-y-4">
              {paymentError && (
                <div className="rounded-lg border border-red-500/30 bg-red-500/10 px-3 py-2 text-sm text-red-300">
                  {paymentError}
                </div>
              )}
              <div>
                <Label htmlFor="payment-source">Cuenta origen</Label>
                <Select
                  value={paymentForm.from_account_id}
                  onValueChange={(v) => setPaymentForm({ ...paymentForm, from_account_id: v })}
                >
                  <SelectTrigger id="payment-source" className="bg-zinc-800 border-zinc-700">
                    <SelectValue placeholder="Selecciona una cuenta" />
                  </SelectTrigger>
                  <SelectContent>
                    {paymentSourceAccounts.map(acc => (
                      <SelectItem key={acc.id} value={acc.id}>
                        {acc.name} · {TYPE_META[acc.type]?.label || acc.type}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label htmlFor="payment-amount">Monto a pagar</Label>
                <Input
                  id="payment-amount"
                  inputMode="decimal"
                  type="number"
                  step="0.01"
                  min="0.01"
                  value={paymentForm.amount}
                  onChange={(e) => setPaymentForm({ ...paymentForm, amount: e.target.value })}
                  className="bg-zinc-800 border-zinc-700"
                  placeholder="0.00"
                  required
                />
              </div>
              <div>
                <Label htmlFor="payment-date">Fecha de pago</Label>
                <Input
                  id="payment-date"
                  type="date"
                  value={paymentForm.date}
                  onChange={(e) => setPaymentForm({ ...paymentForm, date: e.target.value })}
                  className="bg-zinc-800 border-zinc-700"
                  required
                />
              </div>
              <div>
                <Label htmlFor="payment-description">Descripción</Label>
                <Input
                  id="payment-description"
                  value={paymentForm.description}
                  onChange={(e) => setPaymentForm({ ...paymentForm, description: e.target.value })}
                  className="bg-zinc-800 border-zinc-700"
                  placeholder="Pago de tarjeta"
                />
              </div>
              <DialogFooter>
                <Button type="submit" className="bg-emerald-600 hover:bg-emerald-700" disabled={paymentSaving}>
                  {paymentSaving ? 'Registrando...' : 'Registrar pago'}
                </Button>
              </DialogFooter>
            </form>
          </DialogContent>
        </Dialog>
      </main>
    </div>
  );
};

const SidebarNav = ({ active, isAdmin, onClick }) => {
  const { t } = useLanguage();
  const items = [
    { to: '/dashboard',    icon: LayoutDashboard, label: t('dashboard'),      key: 'dashboard' },
    { to: '/transactions', icon: Receipt,         label: t('transactions'),   key: 'transactions' },
    { to: '/accounts',     icon: Wallet,          label: t('accounts'),       key: 'accounts' },
    { to: '/categories',   icon: Tag,             label: t('category'),       key: 'categories' },
    { to: '/recurring',    icon: Repeat,          label: 'Recurrentes',       key: 'recurring' },
    { to: '/budgets',      icon: Target,          label: t('budgets'),        key: 'budgets' },
    { to: '/goals',        icon: PiggyBank,       label: t('goalsAndDebts'),  key: 'goals' },
    { to: '/reports',      icon: BarChart3,       label: t('reports'),        key: 'reports' },
    { to: '/receipts',     icon: ImageIcon,       label: t('receipts'),       key: 'receipts' },
    { to: '/import',       icon: Upload,          label: t('import'),         key: 'import' },
    { to: '/settings',     icon: Settings,        label: t('settings'),       key: 'settings' },
  ];
  return (
    <nav className="space-y-2" aria-label="Navegación principal">
      {items.map(i => (
        <Link key={i.key} to={i.to} onClick={onClick}
          className={`flex items-center gap-3 px-4 py-3 rounded-lg transition-colors ${
            active === i.key
              ? 'bg-violet-500/10 text-violet-400 border border-violet-500/20'
              : 'text-zinc-300 hover:bg-zinc-800/50'
          }`}>
          <i.icon className="h-5 w-5" /> {i.label}
        </Link>
      ))}
      {isAdmin && (
        <Link to="/admin" onClick={onClick}
          className={`flex items-center gap-3 px-4 py-3 rounded-lg transition-colors ${
            active === 'admin'
              ? 'bg-violet-500/10 text-violet-400 border border-violet-500/20'
              : 'text-zinc-300 hover:bg-zinc-800/50'
          }`}>
          <ShieldCheck className="h-5 w-5" /> {t('adminPanelShort')}
        </Link>
      )}
    </nav>
  );
};

// Export the SidebarNav so other pages can reuse it.
export { SidebarNav, TYPE_META };
export default AccountsPage;
